import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://bibleduo-game-production.up.railway.app').replace(/\/$/, '');
const PAGE_SIZE  = 20;

const TABS = [
  { key: 'draft',    label: 'En attente' },
  { key: 'approved', label: 'Approuvées' },
  { key: 'rejected', label: 'Rejetées'   },
];

const TYPE_LABELS = {
  QCM:       'QCM',
  VraiFaux:  'Vrai / Faux',
  Texte:     'Texte libre',
};

// Extrait le count d'un tableau parStatut [{ _id, count }]
const countOf = (parStatut, status) =>
  parStatut?.find(s => s._id === status)?.count ?? 0;

export default function AdminPage() {
  const { user, token } = useAuth();
  const navigate        = useNavigate();

  const [tab,       setTab]       = useState('draft');
  const [questions, setQuestions] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [stats,     setStats]     = useState(null);
  // { [id]: 'approve' | 'reject' } — action en cours
  const [acting,    setActing]    = useState({});
  // Approbation par lot (onglet "En attente")
  const [batchId,      setBatchId]      = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchMsg,     setBatchMsg]     = useState('');

  // Rediriger les non-admins dès que user est connu
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/games', { replace: true });
  }, [user, navigate]);

  // Charger les stats (au montage + après approbation par lot)
  const loadStats = useCallback(() => {
    fetch(`${API_BASE}/api/questions/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setStats(data))
      .catch(() => {});
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Charger les questions quand tab ou page change
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/api/questions?status=${tab}&limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const data = await r.json();
        setQuestions(data.data   ?? []);
        setTotal   (data.total  ?? 0);
      }
    } catch {}
    setLoading(false);
  }, [tab, page, token]);

  useEffect(() => { load(); }, [load]);

  // Changer d'onglet remet la pagination à 0
  const switchTab = (key) => { setTab(key); setPage(0); };

  // Approuver ou rejeter
  const doAction = async (id, verb) => {
    setActing(a => ({ ...a, [id]: verb }));
    try {
      const r = await fetch(`${API_BASE}/api/questions/${id}/${verb}`, {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setQuestions(qs => qs.filter(q => q._id !== id));
        setTotal    (t  => Math.max(0, t - 1));
      }
    } catch {}
    setActing(a => { const n = { ...a }; delete n[id]; return n; });
  };

  // Approuver toutes les questions "draft" d'un batchId
  const approveBatch = async () => {
    const id = batchId.trim();
    if (!id || batchLoading) return;
    setBatchLoading(true);
    setBatchMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/questions/batch/approve`, {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ batchId: id }),
      });
      if (r.ok) {
        const data = await r.json();
        setBatchMsg(`${data.modifiedCount} question(s) approuvée(s)`);
        loadStats();
        load();
      } else {
        setBatchMsg("Échec de l'approbation du lot.");
      }
    } catch {
      setBatchMsg("Échec de l'approbation du lot.");
    }
    setBatchLoading(false);
  };

  if (!user || user.role !== 'admin') return null;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="page-content">
      {/* En-tête */}
      <div style={{ display:'flex', alignItems:'center', gap:'.75rem', marginBottom:'1rem' }}>
        <button className="btn btn-ghost" style={{ padding:'.3rem .5rem' }} onClick={() => navigate('/games')}>←</button>
        <h2 style={{ margin:0, fontSize:'1.2rem' }}>⚙️ Administration</h2>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'.45rem', marginBottom:'1rem' }}>
          {[
            ['En attente', countOf(stats.parStatut,'draft'),    'rgba(255,159,28,.13)', '#ff9f1c'],
            ['Approuvées', countOf(stats.parStatut,'approved'), 'rgba(45,106,79,.15)',  'var(--sage-light)'],
            ['Rejetées',   countOf(stats.parStatut,'rejected'), 'rgba(139,26,26,.15)', 'var(--crimson)'],
          ].map(([label, count, bg, color]) => (
            <div key={label} style={{ background:bg, borderRadius:10, padding:'.55rem', textAlign:'center' }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'1.35rem', color, lineHeight:1 }}>{count}</div>
              <div className="text-tiny" style={{ marginTop:'.2rem' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display:'flex', background:'rgba(255,255,255,.06)', borderRadius:8, padding:3, marginBottom:'1rem', gap:3 }}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            style={{
              flex:1, padding:'.42rem', border:'none', borderRadius:6, cursor:'pointer',
              fontFamily:'var(--font-display)', fontSize:'.78rem',
              background: tab === key ? 'rgba(201,168,76,.22)' : 'transparent',
              color:      tab === key ? 'var(--gold-light)'    : 'var(--gray-400)',
              transition: 'all .15s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Approbation par lot (onglet "En attente" uniquement) */}
      {tab === 'draft' && (
        <div style={{ marginBottom:'1rem' }}>
          <div style={{ display:'flex', gap:'.4rem', flexWrap:'wrap' }}>
            <input
              value={batchId}
              onChange={e => { setBatchId(e.target.value); setBatchMsg(''); }}
              placeholder="bible-questions-lot-01"
              style={{
                flex:1, minWidth:160, padding:'.28rem .65rem', fontSize:'.78rem',
                background:'rgba(255,255,255,.06)', border:'1px solid rgba(201,168,76,.14)',
                borderRadius:8, color:'var(--parch)',
              }}
            />
            <button
              className="btn btn-secondary"
              style={{ fontSize:'.72rem', padding:'.28rem .65rem', opacity: batchLoading ? .5 : 1 }}
              disabled={batchLoading || !batchId.trim()}
              onClick={approveBatch}
            >
              ✅ Valider tout le lot
            </button>
          </div>
          {batchMsg && (
            <p className="text-small" style={{ margin:'.4rem 0 0', color:'var(--sage-light)' }}>
              {batchMsg}
            </p>
          )}
        </div>
      )}

      {/* Liste */}
      {loading && (
        <p className="text-small" style={{ textAlign:'center', padding:'1.5rem', color:'var(--gray-400)' }}>
          Chargement…
        </p>
      )}

      {!loading && questions.length === 0 && (
        <p className="text-small" style={{ textAlign:'center', padding:'1.5rem', color:'var(--gray-400)' }}>
          Aucune question dans cet onglet.
        </p>
      )}

      {!loading && questions.map(q => {
        const busy = !!acting[q._id];
        return (
          <div key={q._id} style={{ background:'rgba(253,246,227,.04)', border:'1px solid rgba(201,168,76,.14)', borderRadius:10, padding:'.8rem', marginBottom:'.45rem' }}>

            {/* Badges méta */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'.3rem', marginBottom:'.45rem' }}>
              {[TYPE_LABELS[q.type] || q.type, q.level, q.testament].filter(Boolean).map(tag => (
                <span key={tag} className="badge-pill" style={{ fontSize:'.65rem' }}>{tag}</span>
              ))}
            </div>

            {/* Texte de la question */}
            <p style={{ fontSize:'.86rem', color:'var(--parch)', margin:'0 0 .4rem', lineHeight:1.55 }}>
              {q.question}
            </p>

            {/* Options (QCM) */}
            {q.options?.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'.28rem', marginBottom:'.4rem' }}>
                {q.options.map(opt => {
                  const correct = String(opt) === String(q.correctAnswer);
                  return (
                    <span key={opt} style={{
                      fontSize:'.72rem', padding:'.18rem .5rem', borderRadius:99,
                      background: correct ? 'rgba(45,106,79,.22)' : 'rgba(255,255,255,.06)',
                      color:      correct ? 'var(--sage-light)'   : 'var(--gray-400)',
                      border:     `1px solid ${correct ? 'rgba(64,145,108,.4)' : 'rgba(255,255,255,.07)'}`,
                    }}>
                      {correct && '✓ '}{opt}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Réponse si pas d'options */}
            {(!q.options || q.options.length === 0) && (
              <p style={{ fontSize:'.75rem', color:'var(--sage-light)', margin:'0 0 .4rem' }}>
                ✓ {String(q.correctAnswer)}
              </p>
            )}

            {/* Boutons */}
            <div style={{ display:'flex', gap:'.4rem', marginTop:'.1rem', flexWrap:'wrap' }}>
              {tab !== 'approved' && (
                <button
                  className="btn btn-secondary"
                  style={{ fontSize:'.72rem', padding:'.28rem .65rem', opacity: busy ? .5 : 1 }}
                  disabled={busy}
                  onClick={() => doAction(q._id, 'approve')}
                >
                  ✅ Approuver
                </button>
              )}
              {tab !== 'rejected' && (
                <button
                  className="btn btn-secondary"
                  style={{ fontSize:'.72rem', padding:'.28rem .65rem', color:'var(--crimson)', borderColor:'rgba(139,26,26,.4)', opacity: busy ? .5 : 1 }}
                  disabled={busy}
                  onClick={() => doAction(q._id, 'reject')}
                >
                  ❌ Rejeter
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'.75rem', padding:'.75rem 0 .25rem' }}>
          <button
            className="btn btn-secondary"
            style={{ padding:'.3rem .7rem', fontSize:'.8rem' }}
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >← Préc</button>
          <span className="text-small">{page + 1} / {totalPages}</span>
          <button
            className="btn btn-secondary"
            style={{ padding:'.3rem .7rem', fontSize:'.8rem' }}
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >Suiv →</button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
